/**
 * Rastreamento unificado de eventos do cliente: Facebook Pixel + Google
 * Analytics (GA4). Os scripts só são injetados se o respectivo ID estiver
 * configurado na loja. Se NENHUM ID estiver setado, tudo vira no-op.
 *
 * Inicializado uma vez no `tenant-bootstrap`, quando a config da loja carrega.
 *
 * Cada evento é disparado nos DOIS provedores com os parâmetros que cada um
 * espera para e-commerce (Meta usa content_ids/contents/content_type; GA4 usa
 * items[]/currency/value). Mantemos os obrigatórios de cada evento sempre
 * presentes para não perder ROAS/otimização.
 */

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  loaded: boolean;
  version: string;
  push: Fbq;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const MOEDA = 'BRL';
/** content_type exigido pela Meta nos eventos de produto. */
const TIPO_CONTEUDO = 'product';

let pixelId: string | null = null;
let gaIdLojista: string | null = null;
let gaIdParseint: string | null = null;
let inicializado = false;
let modulo: ModuloCardapio = 'OUTRO';

/**
 * De onde veio o evento. Vai como parâmetro em TODOS os eventos, para separar
 * no Gerenciador de Eventos e no GA4 o que é venda de fora (delivery) do que é
 * cliente já dentro da loja pedindo pelo celular (mesa/comanda) — sem isso, os
 * dois viram a mesma conversão e o custo por aquisição fica irreal.
 */
export type CanalRastreio = 'DELIVERY' | 'MESA_COMANDA' | 'VITRINE';

/**
 * Canal de rastreio do tipo de acesso, ou `null` para NÃO rastrear.
 *
 * Os aparelhos da casa (totem, totem de checkout e tablet na mesa) ficam de
 * fora: além de o cliente já estar dentro do restaurante, o navegador é
 * compartilhado — um cookie do Pixel para dezenas de clientes, o que suja o
 * público de remarketing em vez de qualificá-lo.
 */
export function canalRastreioDoTipo(tipo: string): CanalRastreio | null {
  switch (tipo) {
    case 'DELIVERY':
      return 'DELIVERY';
    case 'MESA':
    case 'COMANDA':
      return 'MESA_COMANDA';
    case 'VITRINE':
      return 'VITRINE';
    default:
      return null;
  }
}

/**
 * Módulo do cardápio, na medição da PARSEINT. Difere do `CanalRastreio` de
 * propósito: os aparelhos da casa ficam de fora do rastreio do LOJISTA (cookie
 * compartilhado suja o remarketing dele), mas totem e tablet são uso do nosso
 * produto e precisam ser contados.
 */
export type ModuloCardapio = CanalRastreio | 'TOTEM' | 'TOTEM_CHECKOUT' | 'TABLET' | 'OUTRO';

export function moduloDoTipo(tipo: string): ModuloCardapio {
  switch (tipo) {
    case 'DELIVERY':
      return 'DELIVERY';
    case 'MESA':
    case 'COMANDA':
      return 'MESA_COMANDA';
    case 'VITRINE':
      return 'VITRINE';
    case 'TOTEM':
      return 'TOTEM';
    case 'TOTEM_CHECKOUT':
      return 'TOTEM_CHECKOUT';
    case 'TABLET':
      return 'TABLET';
    default:
      return 'OUTRO';
  }
}

/**
 * Valida o Measurement ID de um data stream GA4. O que não casar desliga a
 * medição em vez de carregar uma tag quebrada na tela do cliente.
 */
export function normalizarMeasurementId(valor: string | null | undefined): string | null {
  if (!valor) return null;
  const id = String(valor).trim().toUpperCase();
  return /^G-[A-Z0-9]{6,12}$/.test(id) ? id : null;
}

/**
 * Measurement ID da PARSEINT — o mesmo para todas as lojas, porque mede o uso do
 * NOSSO produto. Não confundir com `configuracao.googleAnalyticsId`, que é o do
 * lojista e mede os clientes dele.
 */
const ID_PARSEINT = normalizarMeasurementId(import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined);

/** Versão publicada, para saber a partir de qual release um número mudou. */
const VERSAO = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '';

/**
 * Item de comércio normalizado, usado para montar os parâmetros de cada
 * provedor. `valor` é o preço UNITÁRIO; `quantidade` default 1.
 */
export interface ItemRastreio {
  id: number | string;
  nome: string;
  valor: number;
  quantidade?: number;
  categoria?: string;
  /** Faixa da tela que ofereceu o item (ex.: "Peça também"); vai como `item_list_name` no GA4. */
  lista?: string
}

function injetarPixel(id: string): void {
  if (window.fbq) return;
  const n = ((...args: unknown[]): void => {
    if (n.callMethod) n.callMethod(...args);
    else n.queue.push(args);
  }) as Fbq;
  n.queue = [];
  n.loaded = true;
  n.version = '2.0';
  n.push = n;
  window.fbq = n;
  window._fbq = window._fbq ?? n;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  n('init', id);
  n('track', 'PageView', { canal_cardapio: modulo });
}

/**
 * Carrega o gtag.js uma vez e registra CADA destino com um `config` próprio.
 *
 * O `config` da Parseint leva as nossas dimensões: parâmetro declarado em
 * `config` acompanha todo evento daquele Measurement ID — e só daquele. É assim
 * que a nossa taxonomia não vaza para a propriedade do lojista.
 */
function injetarGa(destinos: Array<{ id: string; params?: Record<string, unknown> }>): void {
  if (!destinos.length) return;

  if (!window.gtag) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(destinos[0]!.id)}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer ?? [];
    window.gtag = (...args: unknown[]): void => {
      window.dataLayer!.push(args);
    };
    window.gtag('js', new Date());
  }

  for (const destino of destinos) {
    window.gtag!('config', destino.id, destino.params ?? {});
  }
}

/**
 * Inicializa o rastreamento (idempotente). São DOIS rastreamentos independentes:
 *
 *  - o do **lojista** (Pixel + GA da loja), que só liga quando `canalRastreio`
 *    não é nulo — aparelho da casa fica de fora, para não sujar o remarketing dele;
 *  - o da **Parseint** (`VITE_GA_MEASUREMENT_ID`), que liga SEMPRE que houver ID,
 *    inclusive em totem e tablet, porque ali também é uso do nosso produto.
 *
 * Sem nenhum ID configurado, todo evento vira no-op e os pontos de disparo não
 * precisam saber de nada disso.
 */
export function inicializarRastreamento(config: {
  pixelFacebookId: string | null;
  googleAnalyticsId: string | null;
  canalRastreio: CanalRastreio | null;
  modulo: ModuloCardapio;
  unidadeId: number | string | null;
}): void {
  if (inicializado) return;
  inicializado = true;
  modulo = config.modulo;

  const rastreiaLojista = config.canalRastreio !== null;
  pixelId = rastreiaLojista ? config.pixelFacebookId?.trim() || null : null;
  gaIdLojista = rastreiaLojista ? normalizarMeasurementId(config.googleAnalyticsId) : null;
  gaIdParseint = ID_PARSEINT;

  if (pixelId) injetarPixel(pixelId);

  const destinos: Array<{ id: string; params?: Record<string, unknown> }> = [];
  if (gaIdLojista) destinos.push({ id: gaIdLojista });
  if (gaIdParseint) {
    destinos.push({
      id: gaIdParseint,
      params: {
        sistema: 'CARDAPIO',
        publico: 'CONSUMIDOR',
        modulo: config.modulo,
        ...(VERSAO ? { versao: VERSAO } : {}),
        ...(config.unidadeId ? { unidade_id: String(config.unidadeId) } : {}),
      },
    });
  }
  injetarGa(destinos);
}

// ── Backoffice (/admin) ─────────────────────────────────────────────────────
//
// Aqui não existe tag de lojista: a tela é nossa, quem usa é operador da loja e
// tem identidade. Os parâmetros viajam no PRÓPRIO evento, e não num `config`,
// para não disputar configuração com o rastreamento do cliente final quando os
// dois acontecem na mesma sessão do SPA.

let adminInicializado = false;
let adminParams: Record<string, unknown> = {};

/**
 * Liga a medição do backoffice. Idempotente para a tag; os dados de identidade
 * podem ser reenviados quando o `me` carregar.
 *
 * `usuarioId` é o id NUMÉRICO — nunca e-mail ou username (LGPD).
 */
export function inicializarRastreamentoAdmin(dados: {
  usuarioId?: number | null;
  unidadeId?: number | null;
}): void {
  if (!ID_PARSEINT) return;

  adminParams = {
    sistema: 'CARDAPIO',
    publico: 'OPERADOR',
    modulo: 'ADMIN',
    ...(VERSAO ? { versao: VERSAO } : {}),
    ...(dados.unidadeId ? { unidade_id: String(dados.unidadeId) } : {}),
  };

  if (!adminInicializado) {
    adminInicializado = true;
    injetarGa([{ id: ID_PARSEINT, params: { send_page_view: false } }]);
  }

  if (dados.usuarioId && window.gtag) {
    window.gtag('set', { user_id: String(dados.usuarioId) });
  }
}

/** Um page_view por rota do backoffice. */
export function rastrearPaginaAdmin(caminho: string): void {
  if (!adminInicializado || !ID_PARSEINT || !window.gtag) return;
  window.gtag('event', 'page_view', {
    ...adminParams,
    page_path: caminho,
    page_title: document.title,
    send_to: ID_PARSEINT,
  });
}

/** Acrescenta a tag do módulo aos parâmetros do evento. */
function comCanal(dados?: Record<string, unknown>): Record<string, unknown> {
  return { ...dados, canal_cardapio: modulo };
}

function fb(evento: string, dados?: Record<string, unknown>): void {
  if (!pixelId || !window.fbq) return;
  window.fbq('track', evento, comCanal(dados));
}

/** Mesmo evento para os dois destinos; `send_to` decide quem recebe. */
function ga(evento: string, dados?: Record<string, unknown>): void {
  const destinos = [gaIdLojista, gaIdParseint].filter(Boolean) as string[];
  if (!destinos.length || !window.gtag) return;
  window.gtag('event', evento, { ...comCanal(dados), send_to: destinos });
}

// ── Helpers para montar os parâmetros de cada provedor ──────────────────────

function qtd(item: ItemRastreio): number {
  return item.quantidade ?? 1;
}

/** content_ids da Meta — lista de IDs (string). */
function contentIds(itens: ItemRastreio[]): string[] {
  return itens.map((i) => String(i.id));
}

/** contents da Meta — id + quantidade + preço unitário por item. */
function contents(itens: ItemRastreio[]): Array<Record<string, unknown>> {
  return itens.map((i) => ({ id: String(i.id), quantity: qtd(i), item_price: i.valor }));
}

/** items[] do GA4 — campos recomendados de e-commerce. */
function gaItems(itens: ItemRastreio[]): Array<Record<string, unknown>> {
  return itens.map((i) => ({
    item_id: String(i.id),
    item_name: i.nome,
    ...(i.categoria ? { item_category: i.categoria } : {}),
    ...(i.lista ? { item_list_name: i.lista } : {}),
    price: i.valor,
    quantity: qtd(i),
  }));
}

/** num_items da Meta — soma das quantidades. */
function totalItens(itens: ItemRastreio[]): number {
  return itens.reduce((s, i) => s + qtd(i), 0);
}

// ── Eventos ─────────────────────────────────────────────────────────────────

/**
 * Cliente abriu o cardápio (lista de produtos). O PageView já é disparado na
 * inicialização do Pixel; aqui complementamos com a visão de lista no GA4.
 */
export function rastrearAbriuCardapio(nomeLoja?: string): void {
  ga('view_item_list', { item_list_name: nomeLoja });
}

/** Cliente tocou num banner do topo do cardápio; `posicao` começa em 1. Só GA4: a Meta não tem evento equivalente. */
export function rastrearClicouBanner(banner: { id: string; nome?: string | null; posicao: number }): void {
  ga('select_promotion', {
    promotion_id: banner.id,
    ...(banner.nome ? { promotion_name: banner.nome } : {}),
    creative_slot: `banner_${banner.posicao}`,
  })
}

/** Cliente visualizou um produto específico. */
export function rastrearVisualizouProduto(produto: ItemRastreio): void {
  fb('ViewContent', {
    content_ids: contentIds([produto]),
    content_type: TIPO_CONTEUDO,
    content_name: produto.nome,
    content_category: produto.categoria,
    value: produto.valor,
    currency: MOEDA,
  });
  ga('view_item', {
    currency: MOEDA,
    value: produto.valor,
    items: gaItems([produto]),
  });
}

/** Cliente usou a busca do cardápio. (helper pronto p/ quando a busca existir) */
export function rastrearBusca(termo: string, categoria?: string): void {
  fb('Search', {
    search_string: termo,
    content_category: categoria,
    content_type: TIPO_CONTEUDO,
  });
  ga('search', { search_term: termo });
}

/** Cliente adicionou um produto ao carrinho. */
export function rastrearAdicionouProduto(produto: ItemRastreio): void {
  fb('AddToCart', {
    content_ids: contentIds([produto]),
    content_type: TIPO_CONTEUDO,
    content_name: produto.nome,
    contents: contents([produto]),
    value: produto.valor * qtd(produto),
    currency: MOEDA,
  });
  ga('add_to_cart', {
    currency: MOEDA,
    value: produto.valor * qtd(produto),
    items: gaItems([produto]),
  });
}

/** Cliente favoritou um item. (helper pronto p/ quando favoritos existir) */
export function rastrearFavoritou(produto: ItemRastreio): void {
  fb('AddToWishlist', {
    content_ids: contentIds([produto]),
    content_type: TIPO_CONTEUDO,
    content_name: produto.nome,
    value: produto.valor,
    currency: MOEDA,
  });
  ga('add_to_wishlist', {
    currency: MOEDA,
    value: produto.valor,
    items: gaItems([produto]),
  });
}

/** Cliente abriu o checkout para finalizar — início do fechamento. */
export function rastrearIniciouCheckout(itens: ItemRastreio[], valorTotal: number): void {
  fb('InitiateCheckout', {
    value: valorTotal,
    currency: MOEDA,
    num_items: totalItens(itens),
    content_ids: contentIds(itens),
    contents: contents(itens),
    content_type: TIPO_CONTEUDO,
  });
  ga('begin_checkout', {
    currency: MOEDA,
    value: valorTotal,
    items: gaItems(itens),
  });
}

/** Cliente escolheu/preencheu a forma de pagamento. */
export function rastrearInformouPagamento(
  itens: ItemRastreio[],
  valorTotal: number,
  formaPagamento?: string,
): void {
  fb('AddPaymentInfo', {
    value: valorTotal,
    currency: MOEDA,
    content_ids: contentIds(itens),
    content_category: formaPagamento,
  });
  ga('add_payment_info', {
    currency: MOEDA,
    value: valorTotal,
    ...(formaPagamento ? { payment_type: formaPagamento } : {}),
    items: gaItems(itens),
  });
}

/** Pedido criado com sucesso (compra concluída). */
export function rastrearPedidoConcluido(pedido: {
  id: string;
  valor: number;
  itens: ItemRastreio[];
}): void {
  fb('Purchase', {
    value: pedido.valor,
    currency: MOEDA,
    content_ids: contentIds(pedido.itens),
    content_type: TIPO_CONTEUDO,
    contents: contents(pedido.itens),
    num_items: totalItens(pedido.itens),
    order_id: pedido.id,
  });
  ga('purchase', {
    transaction_id: pedido.id,
    currency: MOEDA,
    value: pedido.valor,
    items: gaItems(pedido.itens),
  });
}

/** Cliente concluiu o cadastro/identificação no fluxo. */
export function rastrearCadastroConcluido(): void {
  fb('CompleteRegistration');
  ga('sign_up', { method: 'email' });
}
